import {IErrorResult} from "../../Basic/api";
import {number, z} from "zod";
import moment from "moment";
import {DataForChange} from "../../../enum/personalInfo";
import { ExternalAccount } from "./frontend";

export const Sex = z.object({
    id: z.number().nonnegative(),
    name: z.string()
})

const DateApiModel = z.string().refine((value) => {
    return moment(value, 'YYYY-MM-DD').isValid()
}, 'Invalid date')

const DateApiModelNullable = z.string().refine((value) => {
    return moment(value, 'YYYY-MM-DD').isValid()
}, 'Invalid date').nullable()

export const Person = z.object({
    id: z.string(),
    name: z.string(),
    surname: z.string(),
    patronymic: z.string().nullable(),
    birthdate: DateApiModelNullable,
    sex: Sex.nullable(),
    sex_id: number().nullable()
})

export type IPerson = z.infer<typeof Person>

export interface IPersonResponse {
    data: IPerson
    error: IErrorResult
}

export const User = z.object({
    id: z.string(),
    phone: z.string(),
    email: z.string().email(),
    person: Person
})

export type IUser = z.infer<typeof User>

export interface IUserResponse {
    data: IUser
    error: IErrorResult
}

export interface ISexResponse {
    data: ISex[]
    error: IErrorResult
}

export type ISex = z.infer<typeof Sex>

export const PersonalInfoRequest = z.object({
    name: z.string(),
    surname: z.string(),
    patronymic: z.string().nullable(),
    birthdate: DateApiModelNullable,
    sex_id: z.number().nullable()
})
export type IPersonalInfoRequest = z.infer<typeof PersonalInfoRequest>


export const Snils = z.object({
    value: z.string(),
    is_verified: z.boolean()
})
export type ISnils = z.infer<typeof Snils>

export interface IGetSnilsResponse {
    data: ISnils | null
    error: IErrorResult
}

export interface IGetExternalResponse {
    data: ExternalAccount[],
}

export interface ISnilsRequest {
    snils: string
}

export interface IIdentityCardRequest {
    citizenship_id: number | null
    identity_card_type_id: number | null
    number: string
    issued_at: string
    series: string | null
    issued_by: string | null
    department_code: string | null
    expires_at: string | null
}

export const IdentityCard = z.object({
    id: z.number(),
    person_id: z.string(),
    identity_card_type_id: z.number(),
    identityCardType: z.object({
        id: z.number(),
        name: z.string()
    }),
    citizenship_id: z.number(),
    citizenship: z.object({
        id: z.number(),
        name: z.string().nullable()
    }),
    series: z.string().nullable(),
    number: z.string(),
    issued_by: z.string().nullable(),
    department_code: z.string().nullable(),
    is_verified: z.boolean(),
    issued_at: DateApiModel.nullable(),
    expires_at: DateApiModel.nullable()
})

export type IIdentityCard = z.infer<typeof IdentityCard>

export interface IGetIdentityCardResponse {
    data: IIdentityCard | null,
    error: IErrorResult
}

export const Citizenship = z.object({
    id: z.number(),
    name: z.string().nullable()
})
export const CitizenshipArray = z.array(Citizenship)
export type ICitizenship = z.infer<typeof Citizenship>

export interface IGetCitizenshipResponse {
    data: ICitizenship[],
    error: IErrorResult
}

export const IdentityCardType = z.object({
    id: z.number(),
    name: z.string()
})

export type IIdentityCardType = z.infer<typeof Citizenship>

export const IdentityCardTypes = z.array(IdentityCardType)

export interface IGetIdentityCardTypeResponse {
    data: IIdentityCardType[],
    error: IErrorResult
}

export interface IIdentityCardTypeRequest {
    id: number,
    name: string
}

export interface IAvatar {
    id: number
    path: string
}

export interface IGetAvatarResponse {
    data: IAvatar | null,
    error: IErrorResult
}

export interface IAvatar {
    id: number,
    path: string
}

export const Passport = z.object({
    id: z.number(),
    person_id: z.string(),
    identity_card_type_id: z.number(),
    identityCardType: z.object({
        id: z.number(),
        name: z.string()
    }),
    citizenship_id: z.number(),
    citizenship: z.object({
        id: z.number(),
        name: z.string().nullable()
    }),
    series: z.string(),
    number: z.string(),
    issued_by: z.string().nullable(),
    department_code: z.string().nullable(),
    issued_at: DateApiModel.nullable(),
    is_verified: z.boolean()
})

export const ForeignPassport = z.object({
    id: z.number(),
    person_id: z.string(),
    identity_card_type_id: z.number(),
    identityCardType: z.object({
        id: z.number(),
        name: z.string()
    }),
    citizenship_id: z.number(),
    citizenship: z.object({
        id: z.number(),
        name: z.string().nullable()
    }),
    number: z.string(),
    issued_at: DateApiModel.nullable(),
    expires_at: DateApiModel.nullable(),
    is_verified: z.boolean()
})

export interface IInitiateDataChangeProps {
    data: string
    type_id: DataForChange
}
export interface IRepeatCodeProps {
    change_id :number
}
export interface IConfirmCodeProps {
    change_id :number
    code :string
}

export interface ISetNewPhoneProps {
    phone :string
    phone_confirm :string
}

export const InitiateDataChangeResultModel = z.object({
    change_id: z.number(),
    retry_after: z.number().nullable(),
    data: z.string()
})

export type IInitiateDataChangeData = z.infer<typeof InitiateDataChangeResultModel>

export interface IInitiateDataChangeResult {
    data: IInitiateDataChangeData
    error: IErrorResult
}

export interface IConfirmCodeResult {
    data: null
    error: IErrorResult
}
export interface ISetNewPhoneResult {
    data: null
    error: IErrorResult
}